import type { CameraDef, CameraKind, MatchEvent } from '../core/contracts';

const GOALMOUTH = new Set(['shot', 'shot_on_target', 'save', 'goal', 'near_miss', 'woodwork', 'penalty']);
const CONTACT = new Set(['foul', 'tackle', 'yellow_card', 'red_card', 'injury', 'var_check']);

/**
 * Ranked replay angles for an event — best first. Goalmouth action prefers the
 * 18-yard high camera and behind-goal camera at the end where it happened;
 * contact (fouls/cards) prefers tight + handheld; reverse angle is always a
 * strong second/third angle in replay (180° rule doesn't apply).
 */
export function rankReplayAngles(event: MatchEvent | null, cams: readonly CameraDef[]): string[] {
  const right = (event?.pos.x ?? 0) >= 0;
  let order: CameraKind[];
  if (event && GOALMOUTH.has(event.type)) {
    order = right
      ? ['super_slomo', 'high_18_right', 'behind_goal_right', 'reverse_angle', 'main_tight', 'pitchside_handheld_right', 'main_wide']
      : ['super_slomo', 'high_18_left', 'behind_goal_left', 'reverse_angle', 'main_tight', 'pitchside_handheld_left', 'main_wide'];
  } else if (event && CONTACT.has(event.type)) {
    order = ['super_slomo', 'main_tight', right ? 'pitchside_handheld_right' : 'pitchside_handheld_left', 'reverse_angle', right ? 'high_18_right' : 'high_18_left', 'main_wide'];
  } else {
    order = ['main_tight', 'reverse_angle', 'spidercam', 'main_wide'];
  }
  const out: string[] = [];
  for (const k of order) for (const c of cams) if (c.kind === k && !out.includes(c.id)) out.push(c.id);
  for (const c of cams) if (!out.includes(c.id) && c.kind !== 'crowd' && c.kind !== 'bench') out.push(c.id);
  return out;
}

/** Convenience adapter for MixerOptions.pickReplayAngle. */
export function replayAnglePicker(cams: readonly CameraDef[]) {
  return (event: MatchEvent | null, camIds: readonly string[]): string =>
    rankReplayAngles(event, cams).find((id) => camIds.includes(id)) ?? camIds[0]!;
}
